import React, { useState, useEffect } from 'react';
import { X, MapPin, Building2, Users, Star, Maximize2, Check, X as XIcon, Loader2, Phone, Calendar, Layers, Waves, Car, Sofa } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PropertyService } from '../../handling_data/services/PropertyService';
import { Property } from '../../handling_data/models/Property';

interface PropertyPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    propertyId: number | null;
}

export const PropertyPreviewModal: React.FC<PropertyPreviewModalProps> = ({ isOpen, onClose, propertyId }) => {
    const { t, i18n } = useTranslation();
    const [property, setProperty] = useState<Property | null>(null);
    const [loading, setLoading] = useState(false);
    const [activeImage, setActiveImage] = useState<string>('');

    useEffect(() => {
        if (isOpen && propertyId) {
            fetchProperty();
        } else {
            setProperty(null);
            setActiveImage('');
        }
    }, [isOpen, propertyId]);

    const fetchProperty = async () => {
        if (!propertyId) return;
        try {
            setLoading(true);
            const response = await PropertyService.getProperty(propertyId);
            const data = response.data;
            setProperty(data);
            if (data.main_image?.url) {
                setActiveImage(data.main_image.url);
            } else if (data.media && data.media.length > 0) {
                setActiveImage(data.media[0].url);
            }
        } catch (error) {
            console.error('Failed to fetch property details:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const Feature = ({ icon: Icon, label, value, isBoolean = false }: any) => (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
            <div className="flex items-center gap-2 text-gray-600">
                <Icon className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{label}</span>
            </div>
            <div className="text-sm font-bold text-gray-800">
                {isBoolean ? (
                    value ? <Check className="w-4 h-4 text-green-500" /> : <XIcon className="w-4 h-4 text-red-400" />
                ) : (
                    value || '—'
                )}
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            <div className="relative bg-white w-full max-w-5xl max-h-[90vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white sticky top-0 z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{property?.title || t('common.view_details')}</h2>
                            <p className="text-xs text-gray-500">{t('sidebar.properties')} #{property?.id}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {loading ? (
                    <div className="flex-1 flex flex-col items-center justify-center py-20 text-gray-400 gap-4">
                        <Loader2 className="w-12 h-12 animate-spin text-primary" />
                        <span className="font-medium">جاري تحميل تفاصيل العقار...</span>
                    </div>
                ) : property ? (
                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6">
                            {/* Left Side: Images */}
                            <div className="space-y-4">
                                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 relative group shadow-inner">
                                    {activeImage ? (
                                        <img src={activeImage} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2">
                                            <Building2 className="w-16 h-16 opacity-20" />
                                            <span>لا توجد صور متوفرة</span>
                                        </div>
                                    )}
                                    <div className="absolute top-4 right-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${property.is_active ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'} shadow-lg`}>
                                            {property.is_active ? 'نشط' : 'تحت الصيانة'}
                                        </span>
                                    </div>
                                </div>

                                {/* Image Gallery */}
                                {(property.media && property.media.length > 0) && (
                                    <div className="grid grid-cols-5 gap-2">
                                        {property.media.map((m, idx) => (
                                            <button
                                                key={m.id}
                                                onClick={() => setActiveImage(m.url)}
                                                className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${activeImage === m.url ? 'border-primary ring-2 ring-primary/20 scale-95' : 'border-transparent opacity-70 hover:opacity-100 hover:scale-105'}`}
                                            >
                                                <img src={m.url} alt="" className="w-full h-full object-cover" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Right Side: Details */}
                            <div className="space-y-8">
                                {/* Section: Basic Info */}
                                <div>
                                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">المعلومات الأساسية</h3>
                                    <div className="bg-gray-50/50 p-4 rounded-2xl border border-gray-100 space-y-4">
                                        <div className="flex items-start gap-3">
                                            <MapPin className="w-5 h-5 text-primary mt-0.5" />
                                            <div>
                                                <p className="text-sm font-bold text-gray-800">{property.location?.address}</p>
                                                <p className="text-xs text-gray-500">الإحداثيات: {property.location?.lat}, {property.location?.lng}</p>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-600 leading-relaxed italic border-r-2 border-primary/20 pr-4">
                                            {property.description || 'لا يوجد وصف متاح لهذا العقار.'}
                                        </p>
                                    </div>
                                </div>

                                {/* Section: Pricing */}
                                <div>
                                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">التسعير</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-primary/5 rounded-2xl border border-primary/10">
                                            <p className="text-xs text-primary font-bold mb-1">يومي</p>
                                            <p className="text-xl font-black text-primary">${property.pricing?.daily || 0}</p>
                                        </div>
                                        <div className="p-4 bg-v3-accent/5 rounded-2xl border border-v3-accent/10">
                                            <p className="text-xs text-v3-accent font-bold mb-1">شهري</p>
                                            <p className="text-xl font-black text-v3-accent">${property.pricing?.monthly || 0}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Section: Features */}
                                {property.additional_features && property.additional_features.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">{t('property_form.features.title')}</h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {property.additional_features.map((feature, index) => {
                                                // Map backend labels to translations if available
                                                const translatedLabel = t(`property_form.features.${feature.label}`, { defaultValue: feature.label });

                                                // Pick an icon based on label
                                                let Icon = Star;
                                                const labelLower = feature.label.toLowerCase();
                                                if (labelLower.includes('room') || labelLower.includes('غرف')) Icon = Building2;
                                                else if (labelLower.includes('bath') || labelLower.includes('حمام')) Icon = Building2;
                                                else if (labelLower.includes('area') || labelLower.includes('مساح')) Icon = Maximize2;
                                                else if (labelLower.includes('floor') || labelLower.includes('طابق')) Icon = Layers;
                                                else if (labelLower.includes('pool') || labelLower.includes('مسبح')) Icon = Waves;
                                                else if (labelLower.includes('park') || labelLower.includes('موقف')) Icon = Car;
                                                else if (labelLower.includes('furnish') || labelLower.includes('مفروش')) Icon = Sofa;

                                                let displayValue = feature.value;
                                                if (labelLower.includes('area')) {
                                                    const valueString = String(feature.value);
                                                    if (!valueString.includes('م²') && !valueString.includes('m²') && !valueString.includes('sqm')) {
                                                        displayValue = `${valueString} م²`;
                                                    }
                                                }

                                                return (
                                                    <Feature
                                                        key={index}
                                                        icon={Icon}
                                                        label={translatedLabel}
                                                        value={displayValue}
                                                        isBoolean={typeof feature.value === 'boolean'}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Section: Owner */}
                                <div>
                                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">المالك</h3>
                                    <div className="flex items-center justify-between p-4 bg-secondary/5 rounded-2xl border border-secondary/10">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center text-secondary font-bold text-lg">
                                                {property.owner?.name?.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-800">{property.owner?.name}</p>
                                                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                                    <Phone className="w-3 h-3" />
                                                    <span className="dir-ltr">{property.owner?.phone}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-gray-400">
                                            <Calendar className="w-3 h-3" />
                                            <span>{new Date(property.created_at || '').toLocaleDateString('ar-EG')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Footer Actions */}
                <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 sticky bottom-0 z-10">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-100 transition-colors shadow-sm"
                    >
                        {t('common.cancel')}
                    </button>
                </div>
            </div>
        </div>
    );
};
